import * as React from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  useNavigation,
  isBottomNavRoute,
  updatePageLayoutHeight,
} from "../../utils";
import {
  KeepAliveHomePage,
  createLazyRouteWithPageWrapper,
  createLazyRoute,
  KeepAliveEventCenterPage,
  KeepAliveAgentCenterPage,
  KeepAliveInviteFriendsPage,
  KeepAliveVIPPage,
  KeepAliveProfilePage,
} from "../LazyPage";
import { ROUTES } from "../../types/routes";
import { ProtectedRoute } from "./ProtectedRoute";
// import { GuestRoute } from "./GuestRoute";
import Layout from "../../Layout";
import { useUserStore } from "../../modules/user";
import { useBottomNavInviteTarget } from "../../modules/homeDisplay/hooks";
import { Suspense, useEffect } from "react";
import PageLoading from "../PageLoading";
import { PullToRefresh } from "../Base/PullToRefresh";
import { openLoginRegisterPopup } from "../LoginRegisterPopup";
import { openDepositPopup } from "../DepositPopup/triggerDepositPopup";
import type { CashbackBoostRechargeState } from "../CashbackBoost/types";

// 懒加载的页面组件（带PageWrapper）
const LazyMaintain = createLazyRouteWithPageWrapper(
  () => import("../../pages/Maintain")
);
const LazyAgentCommissionNew = createLazyRouteWithPageWrapper(
  () => import("../../pages/AgentCommissionNew")
);
const LazyWithdraw = createLazyRouteWithPageWrapper(
  () => import("../../pages/Withdraw")
);

const LazyRecordsDeposit = createLazyRouteWithPageWrapper(
  () => import("../../pages/RecordsDeposit")
);
const LazyAccountDetails = createLazyRouteWithPageWrapper(
  () => import("../../pages/AccountDetails")
);
const LazyRedeem = createLazyRouteWithPageWrapper(
  () => import("../../pages/Redeem")
);
const LazyPromotionDetails = createLazyRouteWithPageWrapper(
  () => import("../../pages/PromotionDetails")
);
const LazyRechargePromo = createLazyRouteWithPageWrapper(
  () => import("../../pages/RechargePromo")
);
const LazyEventDetails = createLazyRouteWithPageWrapper(
  () => import("../../pages/EventDetails")
);
const LazyRescueFundRule = createLazyRouteWithPageWrapper(
  () => import("../../pages/RescueFund/RescueFundRule")
);
const LazyMessageCenter = createLazyRouteWithPageWrapper(
  () => import("../../pages/MessageCenter")
);
const LazyMessage = createLazyRoute(
  () => import("../../pages/MessageCenter/part/Message")
);
const LazyGameEmbed = createLazyRouteWithPageWrapper(
  () => import("../../pages/GameEmbed")
);
const LazyGameDetail = createLazyRouteWithPageWrapper(
  () => import("../../pages/GameDetail")
);
const LazyBetbyGame = createLazyRoute(() => import("../../pages/BetbyGame"));
const LazySportsGame = createLazyRoute(() => import("../../pages/SportsGame"));
const LazyGameSearch = createLazyRouteWithPageWrapper(
  () => import("../../pages/GameSearch")
);
const LazyLandingPage = createLazyRouteWithPageWrapper(
  () => import("../../pages/LandingPage")
);
const LazyComponentShowcase = createLazyRouteWithPageWrapper(
  () => import("../../pages/ComponentShowcase")
);
const LazyRedpacket = createLazyRouteWithPageWrapper(
  () => import("../../pages/Redpacket")
);
const LazyUnpaidOrders = createLazyRouteWithPageWrapper(
  () => import("../../pages/UnpaidOrders")
);
const LazyAmoeGetFreeSc = createLazyRouteWithPageWrapper(
  () => import("../../pages/Amoe/GetFreeSc")
);
const LazyPayCvu = createLazyRoute(() => import("../../pages/PayCvu"));
const LazyWinPayCvu = createLazyRoute(() => import("../../pages/WinPayCvu"));

/**
 * 访问 /login 或 /register 时重定向到首页并弹出登录注册底部弹窗
 */
const LoginPopupRedirect: React.FC<{
  defaultTab?: "sms" | "password";
  defaultMode?: "login" | "register";
}> = ({ defaultTab, defaultMode }) => {
  useEffect(() => {
    openLoginRegisterPopup({ defaultTab, defaultMode });
  }, [defaultTab, defaultMode]);
  return <Navigate to={ROUTES.HOME} replace />;
};

const DepositPopupRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const activityId = searchParams.get("activityId") || undefined;
  const cashbackBoost = (
    location.state as { cashbackBoost?: CashbackBoostRechargeState }
  )?.cashbackBoost;
  useEffect(() => {
    openDepositPopup({
      ...(activityId ? { activityId } : {}),
      ...(cashbackBoost?.recordId ? { cashbackBoost } : {}),
    });
  }, [activityId, cashbackBoost]);
  return <Navigate to={ROUTES.HOME} replace />;
};

// 路由配置
const AppRouter: React.FC = () => {
  const { navigate, getCurrentPath } = useNavigation();
  const location = useLocation();
  const inviteTarget = useBottomNavInviteTarget();
  const canRefresh = () => {
    const currentPath = getCurrentPath();
    // 只有首页可以下拉刷新
    return currentPath === ROUTES.HOME || currentPath === ROUTES.HOME_ALT;
  };
  // 判断当前路由是否需要显示 Layout
  const shouldShowLayout = React.useMemo(() => {
    return isBottomNavRoute(location);
  }, [location.pathname]);

  // 监听路由变化，动态设置页面导航栏高度（通过CSS变量）
  React.useEffect(() => {
    updatePageLayoutHeight(location);
  }, [location.pathname, location.state]);

  // 监听全局路由跳转事件
  React.useEffect(() => {
    const handleNavigateToLogin = (event: CustomEvent) => {
      if (
        useUserStore.getState().isAuthenticated() &&
        event.detail.reason === "session_expired"
      ) {
        useUserStore.getState().logout();
      }
    };

    // 添加事件监听器
    window.addEventListener(
      "navigateToLogin",
      handleNavigateToLogin as EventListener
    );

    // 清理事件监听器
    return () => {
      window.removeEventListener(
        "navigateToLogin",
        handleNavigateToLogin as EventListener
      );
    };
  }, [navigate]);

  // 下拉刷新处理函数
  const handleRefresh = async () => {
    // 刷新当前页面
    window.location.reload();
  };

  const routesContent = (
    <Routes>
      {/* 公开路由 - 不需要登录 */}

      {/* 首页路由 */}
      <Route path={ROUTES.HOME} element={<KeepAliveHomePage />} />
      <Route path={ROUTES.HOME_ALT} element={<KeepAliveHomePage />} />

      {/* 用户认证路由 — 重定向到首页并弹出底部登录注册弹窗 */}
      <Route
        path={ROUTES.LOGIN}
        element={
          <LoginPopupRedirect defaultMode="login" defaultTab="password" />
        }
      />
      <Route
        path={ROUTES.REGISTER}
        element={<LoginPopupRedirect defaultTab="sms" />}
      />

      {/* 维护页面路由 */}
      <Route path={ROUTES.REPAIR} element={<LazyMaintain />} />
      <Route path={ROUTES.MAINTAIN} element={<LazyMaintain />} />

      {/* 落地页路由 */}
      <Route path={ROUTES.LANDING_PAGE} element={<LazyLandingPage />} />

      {/* 红包活动路由 */}
      <Route path={ROUTES.REDPACKET} element={<LazyRedpacket />} />

      {/* 受保护的路由 - 需要登录 */}

      {/* VIP页面路由 */}
      <Route
        path={ROUTES.VIP}
        element={
          <ProtectedRoute>
            <KeepAliveVIPPage />
          </ProtectedRoute>
        }
      />
      {/* 用户相关路由 — Profile 页面支持未登录访问，内部做分歧展示 */}
      <Route path={ROUTES.PROFILE} element={<KeepAliveProfilePage />} />
      {inviteTarget !== "INVITE_PAGE" && (
        <Route
          path={ROUTES.AGENT_CENTER}
          element={<KeepAliveAgentCenterPage />}
        />
      )}
      {inviteTarget === "INVITE_PAGE" && (
        <Route
          path={ROUTES.INVITE_FRIENDS}
          element={<KeepAliveInviteFriendsPage />}
        />
      )}
      <Route
        path={ROUTES.AGENT_COMMISSION}
        element={
          <ProtectedRoute>
            <LazyAgentCommissionNew />
          </ProtectedRoute>
        }
      />
      <Route path={ROUTES.RECHARGE} element={<DepositPopupRedirect />} />
      <Route
        path={ROUTES.UNPAID_ORDERS}
        element={
          <ProtectedRoute>
            <LazyUnpaidOrders />
          </ProtectedRoute>
        }
      />
      <Route path={ROUTES.PAY_CVU} element={<LazyPayCvu />} />
      <Route path={ROUTES.WIN_PAY_CVU} element={<LazyWinPayCvu />} />
      <Route
        path={ROUTES.RECHARGE_PROMO}
        element={
          <ProtectedRoute>
            <LazyRechargePromo />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EVENT_DETAILS}
        element={
          <ProtectedRoute>
            <LazyEventDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WITHDRAW}
        element={
          <ProtectedRoute>
            <LazyWithdraw />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.RECORDS_DEPOSIT}
        element={
          <ProtectedRoute>
            <LazyRecordsDeposit />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ACCOUNT_DETAILS}
        element={
          <ProtectedRoute>
            <LazyAccountDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.REDEEM}
        element={
          <ProtectedRoute>
            <LazyRedeem />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROMOTION_DETAILS}
        element={<LazyPromotionDetails />}
      />

      {/* AMOE 合规页。不套 ProtectedRoute：取码动作由后端校验登录态，
          页面本身的规则原文未登录也该能看到。 */}
      <Route path={ROUTES.AMOE_FREE_SC} element={<LazyAmoeGetFreeSc />} />
      <Route
        path={ROUTES.RESCUE_FUND_RULE}
        element={
          <ProtectedRoute>
            <LazyRescueFundRule />
          </ProtectedRoute>
        }
      />

      <Route path={ROUTES.MESSAGE_CENTER} element={<LazyMessageCenter />} />
      <Route
        path={ROUTES.MESSAGE}
        element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoading />}>
              <LazyMessage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EVENT_CENTER}
        element={<KeepAliveEventCenterPage />}
      />

      {/* 游戏相关路由 */}
      <Route path={ROUTES.GAME_SEARCH} element={<LazyGameSearch />} />
      <Route
        path={ROUTES.EMBED}
        element={
          <ProtectedRoute>
            <LazyGameEmbed />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.GAME_DETAIL}
        element={
          <ProtectedRoute>
            <LazyGameDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.BETBY}
        element={
          <ProtectedRoute>
            <LazyBetbyGame />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SPORTS_GAME}
        element={
          <ProtectedRoute>
            <LazySportsGame />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.GAMES}
        element={<Navigate to={ROUTES.HOME} replace />}
      />
      <Route
        path={`${ROUTES.GAMES}/*`}
        element={<Navigate to={ROUTES.HOME} replace />}
      />

      <Route
        path={ROUTES.SETTINGS}
        element={<Navigate to={ROUTES.HOME} replace />}
      />

      {/* 组件展示页（开发调试） */}
      <Route path={ROUTES.COMPONENT_TEST} element={<LazyComponentShowcase />} />

      {/* 默认重定向到首页 */}
      <Route
        path={ROUTES.NOT_FOUND}
        element={<Navigate to={ROUTES.HOME} replace />}
      />
    </Routes>
  );
  // 根据路由条件决定是否使用 Layout 包裹
  return shouldShowLayout ? (
    <Layout cannotRefresh={!canRefresh()}>{routesContent}</Layout>
  ) : (
    <PullToRefresh onRefresh={handleRefresh} canRefresh={canRefresh()}>
      {routesContent}
    </PullToRefresh>
  );
};

export default AppRouter;
